import { withRequestDeadline } from "@softmax/observatory-client/request-signal";
import { apiGet } from "@softmax/observatory-client/client";
import { publicRead } from "@/data/api";
import { ApiError } from "@softmax/observatory-client/transport";
import type { Schemas } from "@softmax/observatory-client/types";
import { coworldManifest } from "@softmax/web-features/coworlds";

import { notFound } from "@tanstack/react-router";
import { getActualViewer } from "@/data/viewer";

import type { CreatorUploadData } from "../../upload/creator-upload";

export function publicUploadData({
  upload,
  unlisted,
}: {
  upload: Schemas["CoworldUploadResponse"];
  unlisted: boolean;
}): CreatorUploadData {
  return {
    kind: "public",
    name: upload.name,
    version: upload.version,
    description: coworldManifest(upload).game.description,
    unlisted,
  };
}

export async function readUpload({
  signal,
  name,
  id,
  publicOnly,
}: {
  signal?: AbortSignal | undefined;
  name: string;
  id: string;
  publicOnly: boolean;
}): Promise<Schemas["CoworldUploadResponse"] | null> {
  const path = `/v2/coworlds/${encodeURIComponent(id)}`;
  const upload = await (
    publicOnly
      ? publicRead<Schemas["CoworldUploadResponse"]>({ path, signal })
      : apiGet<Schemas["CoworldUploadResponse"]>(path, {
          elevatedPrivileges: false,
          signal: withRequestDeadline(signal, 8000),
        })
  ).catch((error: unknown) => {
    if (error instanceof ApiError && error.status === 404) return null;

    throw error;
  });

  if (!upload) return null;

  if (upload.name !== name) throw notFound();

  return upload;
}

export async function readCreatorUpload({
  signal,
  name,
  id,
  unlisted,
}: {
  signal?: AbortSignal | undefined;
  name: string;
  id: string;
  unlisted: boolean;
}): Promise<CreatorUploadData> {
  const upload = await readUpload({ signal, name, id, publicOnly: true });

  if (upload)
    return publicUploadData({
      upload,
      unlisted,
    });

  const viewer = await getActualViewer({ signal });

  if (viewer?.subject_type !== "user" || !viewer.subject_id)
    throw new ApiError("Sign in to view this upload", 401);

  return { kind: "private", name, id, unlisted, viewer };
}
